import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Filter, RefreshCw, Search, 
  Home, CreditCard, FileText, Users, ChevronDown, ChevronUp,
  Calendar, Euro, Building, Download, FileSpreadsheet, Megaphone, PieChart, Activity, UserPlus, UserMinus, MessageCircle, Mail, Check
} from 'lucide-react';
import axios from 'axios';
import { formatStat } from '../utils/format';
import { useModules } from '@/contexts/ModulesContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ csv: false, excel: false });
  const [exportJobs, setExportJobs] = useState([]);
  
  const reportTypeLabels = {
    transactions: 'Transaktionen',
    services: 'Services',
    customers: 'Kunden',
    marketing: 'Marketing'
  };
  
  // Filter states
  const [transactionType, setTransactionType] = useState('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedSellerId, setSelectedSellerId] = useState(''); // NEU: Verkäufer-Filter
  const [customerSearch, setCustomerSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  
  // Data states
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sellers, setSellers] = useState([]); // NEU: Liste der Verkäufer
  const [customerSummary, setCustomerSummary] = useState([]);
  const [summary, setSummary] = useState(null);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [referralStats, setReferralStats] = useState(null); // NEU: Marketing-Statistik
  const [monthlyReferralStats, setMonthlyReferralStats] = useState(null); // NEU: Monatliche Trend-Daten
  const [selectedMarketingYear, setSelectedMarketingYear] = useState(new Date().getFullYear());
  const [returnerImpact, setReturnerImpact] = useState(null); // NEU: Wiederkehrer-Quote + CHF-Impact
  const { isModuleEnabled } = useModules();
  const marketingEnabled = isModuleEnabled('marketing');
  
  // Analytics states
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [quickRange, setQuickRange] = useState('month');
  
  // UI states
  const [activeTab, setActiveTab] = useState('analytics');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  
  // Set default date range (current month)
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setQuickRange('month');
  }, []);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const [customersRes, sellersRes] = await Promise.allSettled([
          axios.get(`${API}/reports/customers`, { headers }),
          axios.get(`${API}/reports/sellers`, { headers })
        ]);

        if (customersRes.status === 'fulfilled') {
          setCustomers(customersRes.value.data || []);
        }
        if (sellersRes.status === 'fulfilled') {
          setSellers(sellersRes.value.data || []);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    loadFilterOptions();
  }, []);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (transactionType && transactionType !== 'all') {
      params.append('transaction_type', transactionType);
    }
    if (selectedCustomerId) {
      params.append('customer_id', selectedCustomerId);
    }
    if (selectedSellerId) {
      params.append('seller_id', selectedSellerId);
    }
    if (customerSearch) {
      params.append('customer_search', customerSearch);
    }
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }
    if (statusFilter) {
      params.append('status', statusFilter);
    }
    if (sortField) {
      params.append('sort_field', sortField);
    }
    if (sortDir) {
      params.append('sort_dir', sortDir);
    }

    return params.toString() ? `?${params.toString()}` : '';
  }, [transactionType, selectedCustomerId, selectedSellerId, customerSearch, startDate, endDate, statusFilter, sortField, sortDir]);

  const loadTabData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    if (activeTab === 'analytics') {
      setAnalyticsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(
          `${API}/reports/analytics-overview?start_date=${startDate}&end_date=${endDate}`,
          { headers }
        );
        setAnalyticsData(response.data || null);
      } catch (error) {
        console.error('Error fetching analytics overview:', error);
        toast.error('Fehler beim Laden des Analytics Dashboards');
      } finally {
        setAnalyticsLoading(false);
        setLoading(false);
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const queryString = buildQueryString();

      const requests = [
        axios.get(`${API}/reports/summary${queryString}`, { headers })
      ];
      const actions = ['summary'];

      if (activeTab === 'transactions') {
        requests.push(axios.get(`${API}/reports/transactions${queryString}`, { headers }));
        actions.push('transactions');
      }

      if (activeTab === 'customers') {
        requests.push(axios.get(`${API}/reports/customer-summary${queryString}`, { headers }));
        actions.push('customerSummary');
      }

      if (activeTab === 'services') {
        requests.push(axios.get(`${API}/reports/service-breakdown?start_date=${startDate}&end_date=${endDate}`, { headers }));
        actions.push('serviceBreakdown');
      }

      if (activeTab === 'marketing') {
        requests.push(axios.get(`${API}/referral-sources/statistics`, { headers }).catch(() => ({ data: null })));
        actions.push('referralStats');
        requests.push(axios.get(`${API}/referral-sources/statistics/monthly?year=${selectedMarketingYear}`, { headers }).catch(() => ({ data: null })));
        actions.push('monthlyReferralStats');
        if (marketingEnabled) {
          requests.push(axios.get(`${API}/reports/returner-impact?period_days=90`, { headers }).catch(() => ({ data: null })));
          actions.push('returnerImpact');
        }
      }

      const results = await Promise.allSettled(requests);
      let hasError = false;

      results.forEach((result, index) => {
        const action = actions[index];
        if (result.status === 'fulfilled') {
          const data = result.value.data;
          if (action === 'summary') setSummary(data || null);
          if (action === 'transactions') setTransactions(data || []);
          if (action === 'customerSummary') setCustomerSummary(data || []);
          if (action === 'serviceBreakdown') setServiceBreakdown(data || []);
          if (action === 'referralStats') setReferralStats(data || null);
          if (action === 'monthlyReferralStats') setMonthlyReferralStats(data || null);
          if (action === 'returnerImpact') setReturnerImpact(data || null);
        } else {
          hasError = true;
        }
      });

      if (hasError) {
        toast.error('Fehler beim Laden der Berichte');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Fehler beim Laden der Berichte');
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildQueryString, startDate, endDate, selectedMarketingYear, marketingEnabled]);

  useEffect(() => {
    if (startDate && endDate) {
      loadTabData();
    }
  }, [loadTabData, startDate, endDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('de-CH').format(value || 0);
  };

  const getHeatmapCellClass = (cell) => {
    const sent = cell?.sent || 0;
    const rate = cell?.conversion_rate || 0;

    if (sent === 0) return 'bg-gray-100 border-gray-200';
    if (rate >= 70) return 'bg-emerald-600 border-emerald-700';
    if (rate >= 45) return 'bg-emerald-500 border-emerald-600';
    if (rate >= 25) return 'bg-amber-500 border-amber-600';
    return 'bg-rose-500 border-rose-600';
  };

  const formatHourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

  const buildLinePath = (data, width, height, valueKey = 'value') => {
    if (!data || data.length === 0) return '';
    const values = data.map((item) => item[valueKey] || 0);
    const maxValue = Math.max(...values, 0);
    const minValue = Math.min(...values, 0);
    const range = maxValue - minValue || 1;

    return data
      .map((item, index) => {
        const x = (index / Math.max(data.length - 1, 1)) * width;
        const normalized = ((item[valueKey] || 0) - minValue) / range;
        const y = height - normalized * height;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { label: 'Bezahlt', variant: 'default', className: 'bg-green-100 text-green-800 border-green-200' },
      pending: { label: 'Ausstehend', variant: 'outline', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      sent: { label: 'Versendet', variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      draft: { label: 'Entwurf', variant: 'outline', className: 'bg-orange-50 text-orange-700 border-orange-200' },
      cancelled: { label: 'Storniert', variant: 'destructive', className: 'bg-red-100 text-red-800 border-red-200' },
      refunded: { label: 'Erstattet', variant: 'outline', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    };
    const config = statusConfig[status] || { label: status, variant: 'outline', className: '' };
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'rent') {
      return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200"><Home size={12} className="mr-1" />Miete</Badge>;
    }
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><FileText size={12} className="mr-1" />Service</Badge>;
  };

  const clearFilters = () => {
    setTransactionType('all');
    setSelectedCustomerId('');
    setSelectedSellerId('');
    setCustomerSearch('');
    setStatusFilter('');
    setSortField('date');
    setSortDir('desc');
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setQuickRange('month');
  };

  const applyQuickRange = (range) => {
    const today = new Date();
    let start;
    let end = new Date(today);

    if (range === 'today') {
      start = new Date(today);
    } else if (range === '7d') {
      start = new Date(today);
      start.setDate(start.getDate() - 6);
    } else if (range === '30d') {
      start = new Date(today);
      start.setDate(start.getDate() - 29);
    } else if (range === '90d') {
      start = new Date(today);
      start.setDate(start.getDate() - 89);
    } else {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setQuickRange(range);
  };

  // Export functions
  const buildExportPayload = (format) => ({
    format,
    report_type: activeTab,
    marketing_year: activeTab === 'marketing' ? selectedMarketingYear : null,
    transaction_type: transactionType !== 'all' ? transactionType : null,
    customer_id: selectedCustomerId || null,
    customer_search: customerSearch || null,
    seller_id: selectedSellerId || null,
    start_date: startDate || null,
    end_date: endDate || null,
    status: statusFilter || null,
    sort_field: sortField,
    sort_dir: sortDir
  });

  const addExportJob = (job) => {
    setExportJobs(prev => [job, ...prev].slice(0, 6));
  };

  const updateExportJob = (jobId, updates) => {
    setExportJobs(prev => prev.map(job => job.id === jobId ? { ...job, ...updates } : job));
  };

  const downloadExportJob = async (jobId, format, reportType = activeTab) => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/reports/export/jobs/${jobId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });

    const disposition = response.headers['content-disposition'];
    const fallbackDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const label = reportTypeLabels[reportType] || 'Report';
    const safeLabel = label.toLowerCase().replace(/\s+/g, '_');
    const fallbackName = `${safeLabel}_${fallbackDate}.${format === 'csv' ? 'csv' : 'xlsx'}`;
    const filename = disposition?.split('filename=')[1]?.replace(/"/g, '') || fallbackName;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const [returnerExporting, setReturnerExporting] = useState('');
  const downloadReturnerImpact = async (format) => {
    try {
      setReturnerExporting(format);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/returner-impact/export?format=${format}&period_days=90`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'];
      const stamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const filename = disposition?.split('filename=')[1]?.replace(/"/g, '') || `wiederkehrer_auswertung_${stamp}.${format}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setReturnerExporting('');
    }
  };

  // Rückhol-Aktion: gefährdete Kunden per E-Mail/WhatsApp einladen
  const [winbackState, setWinbackState] = useState({}); // { [customer_id]: 'sending'|'sent' }
  const sendWinback = async (customer, channel) => {
    const id = customer.customer_id;
    try {
      setWinbackState((s) => ({ ...s, [id]: 'sending' }));
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reports/returner-impact/winback`,
        { customer_id: id, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWinbackState((s) => ({ ...s, [id]: 'sent' }));
      toast.success(`${customer.name} per ${channel === 'email' ? 'E-Mail' : 'WhatsApp'} eingeladen`);
    } catch (e) {
      setWinbackState((s) => { const n = { ...s }; delete n[id]; return n; });
      toast.error(e.response?.data?.detail || 'Einladung konnte nicht gesendet werden');
    }
  };

  const getExportStatusLabel = (status) => {
    if (status === 'completed') return 'Bereit';
    if (status === 'failed') return 'Fehlgeschlagen';
    return 'Wird vorbereitet';
  };

  const formatExportTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('de-CH');
    } catch (error) {
      return '';
    }
  };

  const pollExportJob = async (jobId, format, reportType, attempt = 0) => {
    if (attempt >= 60) {
      toast.error('Export dauert zu lange. Bitte später erneut versuchen.');
      updateExportJob(jobId, { status: 'failed', error: 'Timeout' });
      setExporting(prev => ({ ...prev, [format]: false }));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/export/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const status = response.data.status;
      updateExportJob(jobId, {
        status,
        error: response.data.error || null,
        completedAt: response.data.completed_at || null
      });

      if (status === 'completed') {
        await downloadExportJob(jobId, format, reportType);
        toast.success('Export ist bereit');
        setExporting(prev => ({ ...prev, [format]: false }));
        return;
      }

      if (status === 'failed') {
        toast.error(response.data.error || 'Export fehlgeschlagen');
        setExporting(prev => ({ ...prev, [format]: false }));
        return;
      }
    } catch (error) {
      console.error('Export job status error:', error);
    }

    setTimeout(() => pollExportJob(jobId, format, reportType, attempt + 1), 2000);
  };

  const startExportJob = async (format) => {
    try {
      setExporting(prev => ({ ...prev, [format]: true }));
      const token = localStorage.getItem('token');
      const payload = buildExportPayload(format);
      const reportType = activeTab;
      const reportLabel = reportTypeLabels[reportType] || 'Report';

      const response = await axios.post(`${API}/reports/export/jobs`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      addExportJob({
        id: response.data.job_id,
        status: 'pending',
        format,
        reportType,
        reportLabel,
        createdAt: new Date().toISOString()
      });

      toast.message('Export wird vorbereitet...', { description: `${reportLabel} (${format.toUpperCase()})` });
      pollExportJob(response.data.job_id, format, reportType);
    } catch (error) {
      console.error('Export job error:', error);
      toast.error('Fehler beim Export');
      setExporting(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleExportCSV = async () => {
    await startExportJob('csv');
  };

  const handleExportExcel = async () => {
    await startExportJob('excel');
  };

  const analyticsKpis = analyticsData?.kpis || {};
  const analyticsCards = [
    {
      id: 'total-revenue',
      title: 'Umsatz Zeitraum',
      value: formatCurrency(analyticsKpis.total_revenue),
      subtitle: `${analyticsData?.range?.days || 0} Tage`,
      icon: TrendingUp,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      testId: 'kpi-total-revenue'
    },
    {
      id: 'month-revenue',
      title: 'Monatsumsatz',
      value: formatCurrency(analyticsKpis.month_revenue),
      subtitle: 'aktueller Monat',
      icon: TrendingUp,
      color: 'text-cyan-600',
      bg: 'bg-cyan-50',
      testId: 'kpi-month-revenue'
    },
    {
      id: 'all-time-revenue',
      title: 'Gesamtumsatz',
      value: formatCurrency(analyticsKpis.total_revenue_all_time),
      subtitle: 'gesamt',
      icon: TrendingUp,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      testId: 'kpi-total-revenue-all'
    },
    {
      id: 'open-invoices',
      title: 'Offene Rechnungen',
      value: formatCurrency(analyticsKpis.open_invoices_amount),
      subtitle: `${formatNumber(analyticsKpis.open_invoices_count)} offen`,
      icon: CreditCard,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      testId: 'kpi-open-invoices'
    },
    {
      id: 'active-customers',
      title: 'Aktive Kunden',
      value: formatNumber(analyticsKpis.active_customers),
      subtitle: `${formatNumber(analyticsKpis.total_customers)} gesamt`,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      testId: 'kpi-active-customers'
    },
    {
      id: 'inactive-customers',
      title: 'Inaktive Kunden',
      value: formatNumber(analyticsKpis.inactive_customers),
      subtitle: 'Status inaktiv',
      icon: UserMinus,
      color: 'text-gray-600',
      bg: 'bg-gray-100',
      testId: 'kpi-inactive-customers'
    },
    {
      id: 'new-customers',
      title: 'Neue Kunden',
      value: formatNumber(analyticsKpis.new_customers),
      subtitle: 'im Zeitraum',
      icon: UserPlus,
      color: 'text-green-600',
      bg: 'bg-green-50',
      testId: 'kpi-new-customers'
    },
    {
      id: 'churn-risk',
      title: 'Churn Risiko',
      value: formatNumber(analyticsKpis.churn_risk_customers),
      subtitle: `${analyticsKpis.churn_risk_percent || 0}% Risiko`,
      icon: Activity,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      testId: 'kpi-churn-risk'
    },
    {
      id: 'active-subs',
      title: 'Aktive Abos',
      value: formatNumber(analyticsKpis.active_subscriptions),
      subtitle: 'wiederkehrend',
      icon: CreditCard,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      testId: 'kpi-active-subs'
    },
    {
      id: 'paused-subs',
      title: 'Pausierte Abos',
      value: formatNumber(analyticsKpis.paused_subscriptions),
      subtitle: 'wiederkehrend',
      icon: CreditCard,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      testId: 'kpi-paused-subs'
    },
    {
      id: 'appointments-total',
      title: 'Termine gesamt',
      value: formatNumber(analyticsKpis.appointments_total),
      subtitle: 'im Zeitraum',
      icon: Calendar,
      color: 'text-sky-600',
      bg: 'bg-sky-50',
      testId: 'kpi-appointments-total'
    },
    {
      id: 'appointment-rate',
      title: 'Abschlussquote',
      value: `${analyticsKpis.appointment_completion_rate || 0}%`,
      subtitle: `${formatNumber(analyticsKpis.appointments_completed)} abgeschlossen`,
      icon: Activity,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      testId: 'kpi-appointment-rate'
    },
    {
      id: 'points-consumed',
      title: 'Punkte Verbrauch',
      value: formatNumber(analyticsKpis.points_consumed),
      subtitle: 'im Zeitraum',
      icon: Euro,
      color: 'text-lime-600',
      bg: 'bg-lime-50',
      testId: 'kpi-points-consumed'
    },
    {
      id: 'points-purchased',
      title: 'Punkte gekauft',
      value: formatNumber(analyticsKpis.points_purchased),
      subtitle: 'im Zeitraum',
      icon: Euro,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50',
      testId: 'kpi-points-purchased'
    },
    {
      id: 'subscription-bookings',
      title: 'Abo-Buchungen',
      value: formatNumber(analyticsKpis.subscription_bookings),
      subtitle: 'im Zeitraum',
      icon: CreditCard,
      color: 'text-fuchsia-600',
      bg: 'bg-fuchsia-50',
      testId: 'kpi-subscription-bookings'
    },
    {
      id: 'reengagement-targeted-customers',
      title: 'Re-Engagement Kontakte',
      value: formatNumber(analyticsKpis.reengagement_targeted_customers),
      subtitle: `${formatNumber(analyticsKpis.reengagement_messages_sent)} Nachrichten`,
      icon: MessageCircle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      testId: 'kpi-reengagement-targeted-customers'
    },
    {
      id: 'reengagement-conversion-rate',
      title: 'Re-Engagement Conversion',
      value: `${analyticsKpis.reengagement_conversion_rate || 0}%`,
      subtitle: `${formatNumber(analyticsKpis.reengagement_converted_customers)} Kunden konvertiert`,
      icon: TrendingUp,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      testId: 'kpi-reengagement-conversion-rate'
    }
  ];

  const appointmentTrend = analyticsData?.trends?.appointments || [];
  const appointmentMax = Math.max(...appointmentTrend.map((item) => item.total || 0), 1);
  const newCustomersTrend = analyticsData?.trends?.new_customers || [];
  const newCustomersMax = Math.max(...newCustomersTrend.map((item) => item.value || 0), 1);

  const customerStatus = analyticsData?.breakdowns?.customer_status || { active: 0, inactive: 0 };
  const customerTotal = (customerStatus.active || 0) + (customerStatus.inactive || 0);

  const subscriptionStatus = analyticsData?.breakdowns?.subscription_status || { active: 0, paused: 0, inactive: 0 };
  const subscriptionTotal = (subscriptionStatus.active || 0) + (subscriptionStatus.paused || 0) + (subscriptionStatus.inactive || 0);

  const appointmentStatus = analyticsData?.breakdowns?.appointment_status || {};
  const appointmentStatusTotal = Object.values(appointmentStatus).reduce((sum, value) => sum + (value || 0), 0);

  const reEngagementBreakdown = analyticsData?.breakdowns?.reengagement_conversion || {
    first_stage: { sent: 0, booked: 0, subscribed: 0, converted: 0 },
    second_stage: { sent: 0, booked: 0, subscribed: 0, converted: 0 },
    overall: {
      sent: 0,
      converted_events: 0,
      converted_customers: 0,
      targeted_customers: 0,
      booking_conversions: 0,
      subscription_conversions: 0,
      conversion_rate: 0
    }
  };

  const reEngagementTrend = analyticsData?.trends?.reengagement_conversion || [];
  const reEngagementTrendMaxSent = Math.max(...reEngagementTrend.map((item) => item.sent || 0), 1);
  const reEngagementHeatmapRows = reEngagementBreakdown?.heatmap?.rows || [];
  const reEngagementBestSlots = reEngagementBreakdown?.heatmap?.best_slots || [];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-purple-600" />
            Berichte & Analytics
          </h1>
          <p className="text-gray-600 mt-1">Analytics Dashboard, Transaktionen und Finanzberichte</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {activeTab !== 'analytics' && (
            <>
              <Button 
                variant="outline" 
                onClick={handleExportCSV} 
                disabled={loading || exporting.csv}
                data-testid="export-csv-btn"
              >
                <Download size={16} className={`mr-2 ${exporting.csv ? 'animate-spin' : ''}`} />
                CSV ({reportTypeLabels[activeTab] || 'Report'})
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportExcel} 
                disabled={loading || exporting.excel}
                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet size={16} className={`mr-2 ${exporting.excel ? 'animate-spin' : ''}`} />
                Excel ({reportTypeLabels[activeTab] || 'Report'})
              </Button>
            </>
          )}
          <Button variant="outline" onClick={loadTabData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {activeTab !== 'analytics' && exportJobs.length > 0 && (
        <Card className="mb-6" data-testid="export-status-panel">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-lg">Export-Status</CardTitle>
              <CardDescription>Übersicht der letzten Exporte</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExportJobs([])} data-testid="export-clear-jobs">
              Liste leeren
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {exportJobs.map((job, index) => (
              <div key={job.id} className="border rounded-lg p-3" data-testid={`export-job-${job.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="font-medium" data-testid={`export-job-title-${job.id}`}>
                      {job.reportLabel || reportTypeLabels[job.reportType] || 'Report'}
                      <span className="text-gray-500"> • {job.format?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-gray-500" data-testid={`export-job-time-${job.id}`}>
                      {formatExportTime(job.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${job.status === 'completed' ? 'bg-green-100 text-green-700' : job.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}
                      data-testid={`export-job-status-${job.id}`}
                    >
                      {getExportStatusLabel(job.status)}
                    </span>
                    {job.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadExportJob(job.id, job.format, job.reportType)}
                        data-testid={`export-job-download-${job.id}`}
                      >
                        Download
                      </Button>
                    )}
                  </div>
                </div>
                {job.status === 'failed' && job.error && (
                  <div className="text-xs text-red-600 mt-2" data-testid={`export-job-error-${job.id}`}>
                    {job.error}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {activeTab === 'analytics' && (
        <Card className="mb-6" data-testid="analytics-filter-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter size={18} />
              Zeitraum
            </CardTitle>
            <CardDescription>Wähle einen Schnellfilter oder setze den Zeitraum manuell.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="analytics-quick-range">
              <Button
                variant={quickRange === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyQuickRange('today')}
                data-testid="quick-range-today"
              >
                Heute
              </Button>
              <Button
                variant={quickRange === '7d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyQuickRange('7d')}
                data-testid="quick-range-7d"
              >
                7 Tage
              </Button>
              <Button
                variant={quickRange === '30d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyQuickRange('30d')}
                data-testid="quick-range-30d"
              >
                30 Tage
              </Button>
              <Button
                variant={quickRange === '90d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyQuickRange('90d')}
                data-testid="quick-range-90d"
              >
                90 Tage
              </Button>
              <Button
                variant={quickRange === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyQuickRange('month')}
                data-testid="quick-range-month"
              >
                Monat
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Von</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setQuickRange('custom');
                  }}
                  data-testid="analytics-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Bis</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setQuickRange('custom');
                  }}
                  data-testid="analytics-end-date"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Section */}
      {activeTab !== 'analytics' && (
        <Card className="mb-6" data-testid="filter-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter size={18} />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={transactionType} onValueChange={setTransactionType} data-testid="type-filter">
                <SelectTrigger>
                  <SelectValue placeholder="Alle Typen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alles</SelectItem>
                  <SelectItem value="rent">Miete</SelectItem>
                  <SelectItem value="services">Dienstleistungen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Filter */}
            <div className="space-y-2">
              <Label>Kunde</Label>
              <Select value={selectedCustomerId || "all_customers"} onValueChange={(val) => setSelectedCustomerId(val === "all_customers" ? "" : val)} data-testid="customer-filter">
                <SelectTrigger>
                  <SelectValue placeholder="Alle Kunden" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_customers">Alle Kunden</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id || customer.user_id} value={customer.id || customer.user_id}>
                      {customer.name} {customer.type === 'mieter' ? '(Mieter)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Seller/Trainer Filter - NEU */}
            <div className="space-y-2">
              <Label>Verkäufer</Label>
              <Select value={selectedSellerId || "all_sellers"} onValueChange={(val) => setSelectedSellerId(val === "all_sellers" ? "" : val)} data-testid="seller-filter">
                <SelectTrigger>
                  <SelectValue placeholder="Alle Verkäufer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_sellers">Alle Verkäufer</SelectItem>
                  {sellers.map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.name} {seller.role === 'trainer' ? '(Trainer)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter || "all_status"} onValueChange={(val) => setStatusFilter(val === "all_status" ? "" : val)} data-testid="status-filter">
                <SelectTrigger>
                  <SelectValue placeholder="Alle Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_status">Alle Status</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="sent">Versendet</SelectItem>
                  <SelectItem value="draft">Entwurf</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sortierung */}
            <div className="space-y-2">
              <Label>Sortieren nach</Label>
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger data-testid="reports-sort-field-select">
                  <SelectValue placeholder="Sortieren" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Datum</SelectItem>
                  <SelectItem value="amount">Betrag</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Richtung</Label>
              <Select value={sortDir} onValueChange={setSortDir} data-testid="reports-sort-dir-select">
                <SelectTrigger>
                  <SelectValue placeholder="Richtung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Absteigend</SelectItem>
                  <SelectItem value="asc">Aufsteigend</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label>Von</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setQuickRange('custom');
                }}
                data-testid="start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Bis</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setQuickRange('custom');
                }}
                data-testid="end-date"
              />
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Filter zurücksetzen
            </Button>
            <p className="text-sm text-gray-500">
              {transactions.length} Transaktionen gefunden
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Summary Cards */}
      {activeTab !== 'analytics' && summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
          {/* Total */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Gesamt (inkl. offen)</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_amount)}</p>
                  <p className="text-xs text-gray-400">{summary.total_count} Transaktionen · Umsatz = „Bezahlt"</p>
                </div>
                <TrendingUp className="h-10 w-10 text-blue-300" />
              </div>
            </CardContent>
          </Card>

          {/* Paid */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Bezahlt</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.paid_amount)}</p>
                  <p className="text-xs text-gray-400">{summary.paid_count} Transaktionen</p>
                </div>
                <CreditCard className="h-10 w-10 text-green-300" />
              </div>
            </CardContent>
          </Card>

          {/* Pending */}
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Offen</p>
                  <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.pending_amount)}</p>
                  <p className="text-xs text-gray-400">{summary.pending_count} Transaktionen</p>
                </div>
                <FileText className="h-10 w-10 text-yellow-300" />
              </div>
            </CardContent>
          </Card>

          {/* By Type */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Nach Typ</p>
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs flex items-center gap-1"><Home size={12} /> Miete</span>
                    <span className="font-medium text-sm">{formatCurrency(summary.rent_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs flex items-center gap-1"><FileText size={12} /> Services</span>
                    <span className="font-medium text-sm">{formatCurrency(summary.services_amount)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 size={16} className="mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">
            <FileText size={16} className="mr-2" />
            Transaktionen
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">
            <BarChart3 size={16} className="mr-2" />
            Pro Service
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Users size={16} className="mr-2" />
            Pro Kunde
          </TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">
            <Megaphone size={16} className="mr-2" />
            Marketing
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="space-y-6" data-testid="analytics-dashboard">
            {loading || analyticsLoading ? (
              <div className="text-center py-12 text-gray-500" data-testid="analytics-loading">Laden...</div>
            ) : !analyticsData ? (
              <div className="text-center py-12 text-gray-500" data-testid="analytics-empty">Keine Daten verfügbar</div>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 size={18} />
                      Analytics Überblick
                    </CardTitle>
                    <CardDescription data-testid="analytics-range">
                      Zeitraum: {analyticsData.range?.start_date} – {analyticsData.range?.end_date} ({analyticsData.range?.days} Tage)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2 text-sm text-gray-600">
                    <Badge variant="outline" data-testid="analytics-churn-threshold">
                      Churn-Risiko ab {analyticsData.kpis?.churn_risk_threshold_days} Tagen Inaktivität
                    </Badge>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="analytics-kpi-grid">
                  {analyticsCards.map((card) => {
                    const Icon = card.icon;
                    return (
                      <Card key={card.id} data-testid={card.testId}>
                        <CardContent className="pt-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-gray-500">{card.title}</p>
                              <p className="text-2xl font-bold mt-1" data-testid={`${card.testId}-value`}>
                                {card.value}
                              </p>
                              {card.subtitle && (
                                <p className="text-xs text-gray-400 mt-1">{card.subtitle}</p>
                              )}
                            </div>
                            <div className={`${card.bg} ${card.color} p-2 rounded-full`}>
                              <Icon size={18} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <Card data-testid="analytics-reengagement-conversion-card">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageCircle size={18} />
                      Re-Engagement Conversion Tracking
                    </CardTitle>
                    <CardDescription>
                      Nachricht → Buchung/Abo im gewählten Zeitraum ({formatNumber(reEngagementBreakdown.overall.sent)} gesendete Re-Engagement Nachrichten)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5" data-testid="analytics-reengagement-conversion-content">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-reengagement-conversion-overview-grid">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="analytics-reengagement-booking-conversions-card">
                        <p className="text-xs text-emerald-700">Konvertiert zu Buchung</p>
                        <p className="text-2xl font-bold text-emerald-900" data-testid="analytics-reengagement-booking-conversions-value">
                          {formatNumber(reEngagementBreakdown.overall.booking_conversions)}
                        </p>
                      </div>
                      <div className="rounded-lg border border-violet-200 bg-violet-50 p-4" data-testid="analytics-reengagement-subscription-conversions-card">
                        <p className="text-xs text-violet-700">Konvertiert zu Abo</p>
                        <p className="text-2xl font-bold text-violet-900" data-testid="analytics-reengagement-subscription-conversions-value">
                          {formatNumber(reEngagementBreakdown.overall.subscription_conversions)}
                        </p>
                      </div>
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4" data-testid="analytics-reengagement-conversion-rate-card">
                        <p className="text-xs text-rose-700">Conversion Rate (Kunden)</p>
                        <p className="text-2xl font-bold text-rose-900" data-testid="analytics-reengagement-conversion-rate-value">
                          {reEngagementBreakdown.overall.conversion_rate || 0}%
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border p-4" data-testid="analytics-reengagement-stage-first-card">
                        <p className="font-medium text-sm mb-3">1. Stufe (30 Tage)</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Gesendet</span><strong>{formatNumber(reEngagementBreakdown.first_stage.sent)}</strong></div>
                          <div className="flex justify-between"><span>Buchung</span><strong>{formatNumber(reEngagementBreakdown.first_stage.booked)}</strong></div>
                          <div className="flex justify-between"><span>Abo</span><strong>{formatNumber(reEngagementBreakdown.first_stage.subscribed)}</strong></div>
                          <div className="flex justify-between"><span>Konvertiert</span><strong>{formatNumber(reEngagementBreakdown.first_stage.converted)}</strong></div>
                        </div>
                      </div>
                      <div className="rounded-lg border p-4" data-testid="analytics-reengagement-stage-second-card">
                        <p className="font-medium text-sm mb-3">2. Stufe (60 Tage)</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Gesendet</span><strong>{formatNumber(reEngagementBreakdown.second_stage.sent)}</strong></div>
                          <div className="flex justify-between"><span>Buchung</span><strong>{formatNumber(reEngagementBreakdown.second_stage.booked)}</strong></div>
                          <div className="flex justify-between"><span>Abo</span><strong>{formatNumber(reEngagementBreakdown.second_stage.subscribed)}</strong></div>
                          <div className="flex justify-between"><span>Konvertiert</span><strong>{formatNumber(reEngagementBreakdown.second_stage.converted)}</strong></div>
                        </div>
                      </div>
                    </div>

                    <div data-testid="analytics-reengagement-trend-bars">
                      <p className="text-sm text-gray-600 mb-2">Sende-Tage (gesendet vs. konvertiert)</p>
                      {reEngagementTrend.length ? (
                        <div className="flex items-end gap-1 h-28">
                          {reEngagementTrend.map((item, index) => {
                            const sentHeight = Math.max(4, ((item.sent || 0) / reEngagementTrendMaxSent) * 100);
                            const convertedHeight = Math.max(3, ((item.converted || 0) / reEngagementTrendMaxSent) * 100);
                            return (
                              <div key={`${item.date}-${index}`} className="flex-1 flex items-end">
                                <div className="w-full bg-amber-100 rounded-sm relative" style={{ height: `${sentHeight}%` }}>
                                  <div
                                    className="absolute bottom-0 left-0 right-0 bg-rose-500 rounded-sm"
                                    style={{ height: `${convertedHeight}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Keine Re-Engagement Daten im Zeitraum</div>
                      )}
                    </div>

                    <div className="space-y-3" data-testid="analytics-reengagement-heatmap-card">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-700" data-testid="analytics-reengagement-heatmap-title">
                          Heatmap (Wochentag × Stunde)
                        </p>
                        <div className="flex items-center gap-2 text-xs" data-testid="analytics-reengagement-heatmap-legend">
                          <span className="px-2 py-0.5 rounded bg-gray-100">keine Daten</span>
                          <span className="px-2 py-0.5 rounded bg-rose-500 text-white">niedrig</span>
                          <span className="px-2 py-0.5 rounded bg-amber-500 text-white">mittel</span>
                          <span className="px-2 py-0.5 rounded bg-emerald-600 text-white">hoch</span>
                        </div>
                      </div>

                      {reEngagementBestSlots.length > 0 && (
                        <div className="rounded-lg border bg-emerald-50 p-3" data-testid="analytics-reengagement-best-slots">
                          <p className="text-xs text-emerald-700 mb-2">Beste Zeitfenster (nach Conversion)</p>
                          <div className="flex flex-wrap gap-2">
                            {reEngagementBestSlots.map((slot, idx) => (
                              <span
                                key={`slot-${idx}-${slot.weekday}-${slot.hour}`}
                                className="text-xs px-2 py-1 rounded-full bg-white border border-emerald-200"
                                data-testid={`analytics-reengagement-best-slot-${idx}`}
                              >
                                {slot.weekday_label} {formatHourLabel(slot.hour)} · {slot.conversion_rate}% ({slot.converted}/{slot.sent})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="overflow-x-auto" data-testid="analytics-reengagement-heatmap-grid-wrapper">
                        {reEngagementHeatmapRows.length ? (
                          <div className="min-w-[920px] space-y-2" data-testid="analytics-reengagement-heatmap-grid">
                            {reEngagementHeatmapRows.map((row) => (
                              <div key={`weekday-${row.weekday}`} className="grid grid-cols-[120px_1fr] gap-2 items-center" data-testid={`analytics-reengagement-heatmap-row-${row.weekday}`}>
                                <div className="text-xs text-gray-600" data-testid={`analytics-reengagement-heatmap-row-label-${row.weekday}`}>
                                  <div className="font-medium text-gray-700">{row.weekday_label}</div>
                                  <div>{row.sent} gesendet · {row.conversion_rate}%</div>
                                </div>
                                <div
                                  className="grid gap-1"
                                  style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}
                                  data-testid={`analytics-reengagement-heatmap-row-cells-${row.weekday}`}
                                >
                                  {row.hours.map((cell) => (
                                    <div
                                      key={`cell-${row.weekday}-${cell.hour}`}
                                      className={`h-5 rounded border ${getHeatmapCellClass(cell)}`}
                                      title={`${row.weekday_label} ${formatHourLabel(cell.hour)} | gesendet: ${cell.sent}, konvertiert: ${cell.converted}, rate: ${cell.conversion_rate}%`}
                                      data-testid={`analytics-reengagement-heatmap-cell-${row.weekday}-${cell.hour}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500" data-testid="analytics-reengagement-heatmap-empty">
                            Keine Heatmap-Daten im Zeitraum.
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card data-testid="analytics-revenue-chart">
                    <CardHeader>
                      <CardTitle className="text-base">Umsatz Trend</CardTitle>
                      <CardDescription>{formatCurrency(analyticsData.kpis?.total_revenue)} im Zeitraum</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {analyticsData.trends?.revenue?.length ? (
                        <svg viewBox="0 0 320 120" className="w-full h-32">
                          <path
                            d={buildLinePath(analyticsData.trends.revenue, 320, 120)}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2"
                          />
                        </svg>
                      ) : (
                        <div className="text-sm text-gray-500">Keine Umsatzdaten</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="analytics-appointments-chart">
                    <CardHeader>
                      <CardTitle className="text-base">Termine Trend</CardTitle>
                      <CardDescription>{formatNumber(analyticsData.kpis?.appointments_total)} Termine</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {appointmentTrend.length ? (
                        <div className="flex items-end gap-0.5 h-32" data-testid="analytics-appointments-bars">
                          {appointmentTrend.map((item, index) => {
                            const totalHeight = Math.max(6, (item.total / appointmentMax) * 100);
                            const completedHeight = Math.max(4, (item.completed / appointmentMax) * 100);
                            return (
                              <div key={`${item.date}-${index}`} className="flex-1 flex items-end">
                                <div className="w-full bg-blue-100 rounded-sm relative" style={{ height: `${totalHeight}%` }}>
                                  <div
                                    className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-sm"
                                    style={{ height: `${completedHeight}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Keine Termin-Daten</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="analytics-new-customers-chart">
                    <CardHeader>
                      <CardTitle className="text-base">Neue Kunden Trend</CardTitle>
                      <CardDescription>{formatNumber(analyticsData.kpis?.new_customers)} neue Kunden</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {newCustomersTrend.length ? (
                        <div className="flex items-end gap-0.5 h-32" data-testid="analytics-new-customers-bars">
                          {newCustomersTrend.map((item, index) => {
                            const height = Math.max(6, (item.value / newCustomersMax) * 100);
                            return (
                              <div key={`${item.date}-${index}`} className="flex-1 flex items-end">
                                <div className="w-full bg-emerald-400 rounded-sm" style={{ height: `${height}%` }} />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Keine Kunden-Daten</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card data-testid="analytics-customer-status">
                    <CardHeader>
                      <CardTitle className="text-base">Kundenstatus</CardTitle>
                      <CardDescription>{formatNumber(customerTotal)} Kunden gesamt</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>Aktiv</span>
                          <span>{formatNumber(customerStatus.active)}</span>
                        </div>
                        <Progress value={customerTotal ? (customerStatus.active / customerTotal) * 100 : 0} />
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>Inaktiv</span>
                          <span>{formatNumber(customerStatus.inactive)}</span>
                        </div>
                        <Progress value={customerTotal ? (customerStatus.inactive / customerTotal) * 100 : 0} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="analytics-subscription-status">
                    <CardHeader>
                      <CardTitle className="text-base">Abo-Status</CardTitle>
                      <CardDescription>{formatNumber(subscriptionTotal)} Abos gesamt</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>Aktiv</span>
                          <span>{formatNumber(subscriptionStatus.active)}</span>
                        </div>
                        <Progress value={subscriptionTotal ? (subscriptionStatus.active / subscriptionTotal) * 100 : 0} />
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>Pausiert</span>
                          <span>{formatNumber(subscriptionStatus.paused)}</span>
                        </div>
                        <Progress value={subscriptionTotal ? (subscriptionStatus.paused / subscriptionTotal) * 100 : 0} />
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>Inaktiv</span>
                          <span>{formatNumber(subscriptionStatus.inactive)}</span>
                        </div>
                        <Progress value={subscriptionTotal ? (subscriptionStatus.inactive / subscriptionTotal) * 100 : 0} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="analytics-appointment-status">
                    <CardHeader>
                      <CardTitle className="text-base">Termin-Status</CardTitle>
                      <CardDescription>{formatNumber(appointmentStatusTotal)} Termine im Zeitraum</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(appointmentStatus).map(([status, value]) => (
                        <div key={status}>
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span>{status}</span>
                            <span>{formatNumber(value)}</span>
                          </div>
                          <Progress value={appointmentStatusTotal ? (value / appointmentStatusTotal) * 100 : 0} />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Transaktionsliste</CardTitle>
              <CardDescription>Alle Rechnungen im ausgewählten Zeitraum</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Laden...</div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine Transaktionen gefunden
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="transactions-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Typ</TableHead>
                        <TableHead>Rechnung</TableHead>
                        <TableHead>Kunde</TableHead>
                        <TableHead>Verkäufer</TableHead>
                        <TableHead>Beschreibung</TableHead>
                        <TableHead className="text-right">Betrag</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Erstellt</TableHead>
                        <TableHead>Bezahlt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                          <TableCell>{getTypeBadge(tx.type)}</TableCell>
                          <TableCell className="font-mono text-sm">{tx.invoice_number}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{tx.customer_name}</p>
                              <p className="text-xs text-gray-500">{tx.customer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{tx.sold_by_name || '-'}</span>
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={tx.description}>
                            {tx.description}
                          </TableCell>
                          <TableCell className="text-right">
                            {tx.discount_amount > 0 ? (
                              <div>
                                <span className="line-through text-gray-400 text-sm">
                                  {formatCurrency(tx.amount)}
                                </span>
                                <br />
                                <span className="font-semibold">{formatCurrency(tx.final_amount)}</span>
                              </div>
                            ) : (
                              <span className="font-semibold">{formatCurrency(tx.final_amount)}</span>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(tx.status)}</TableCell>
                          <TableCell className="text-sm">{formatDate(tx.created_at)}</TableCell>
                          <TableCell className="text-sm">
                            {tx.paid_at ? formatDate(tx.paid_at) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle>Umsatz pro Dienstleistung</CardTitle>
              <CardDescription>
                Ist-Trainings, Punktewert &amp; direkte CHF-Einnahmen pro Dienstleistung
                {serviceBreakdown[0]?.price_per_point ? ` · Punktwert: CHF ${serviceBreakdown[0].price_per_point.toFixed(2)}/Punkt (Ø aktive Pakete)` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Laden...</div>
              ) : serviceBreakdown.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine Service-Daten gefunden
                </div>
              ) : (
                <div className="space-y-3" data-testid="service-breakdown-list">
                  {serviceBreakdown.map((service, index) => {
                    const maxCount = Math.max(...serviceBreakdown.map(s => s.count || 0));
                    const percentage = maxCount > 0 ? ((service.count || 0) / maxCount) * 100 : 0;
                    
                    return (
                      <div
                        key={service.service_name || index}
                        className="border rounded-lg p-4"
                        data-testid={`service-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                              <BarChart3 className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium">{service.service_name}</p>
                              <p className="text-sm text-gray-500">
                                <span data-testid={`service-ist-trainings-${index}`}>{service.count}</span> Ist-Training{service.count !== 1 ? 's' : ''} • {service.points_per_training ?? service.points_cost ?? 0} P/Training
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-emerald-600" data-testid={`service-direct-chf-${index}`}>{formatCurrency(service.direct_chf || 0)}</p>
                            <p className="text-xs text-gray-400">direkte CHF-Einnahmen</p>
                          </div>
                        </div>

                        {/* Kennzahlen-Raster */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                          <div className="rounded-md bg-gray-50 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">Punkte gesamt</p>
                            <p className="text-sm font-semibold">{service.points_used || 0}</p>
                          </div>
                          <div className="rounded-md bg-gray-50 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">Einnahmen (Punktewert)</p>
                            <p className="text-sm font-semibold" data-testid={`service-points-value-${index}`}>{formatCurrency(service.points_value_chf ?? service.amount ?? 0)}</p>
                          </div>
                          <div className="rounded-md bg-gray-50 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">Ø CHF/Training</p>
                            <p className="text-sm font-semibold">{formatCurrency(service.avg_chf_per_training || 0)}</p>
                          </div>
                          <div className="rounded-md bg-gray-50 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">CHF/Punkt</p>
                            <p className="text-sm font-semibold">CHF {(service.price_per_point || 0).toFixed(2)}</p>
                          </div>
                        </div>

                        {/* Progress bar (relativ nach Ist-Trainings) */}
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Total */}
                  <div className="border-t pt-4 mt-4" data-testid="service-breakdown-totals">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-gray-400">Ist-Trainings gesamt</p>
                        <p className="text-lg font-bold" data-testid="total-ist-trainings">
                          {serviceBreakdown.reduce((sum, s) => sum + (s.count || 0), 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Punkte verbraucht</p>
                        <p className="text-lg font-bold">
                          {serviceBreakdown.reduce((sum, s) => sum + (s.points_used || 0), 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Einnahmen (Punktewert)</p>
                        <p className="text-lg font-bold" data-testid="total-points-value">
                          {formatCurrency(serviceBreakdown.reduce((sum, s) => sum + (s.points_value_chf ?? s.amount ?? 0), 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">direkte CHF-Einnahmen</p>
                        <p className="text-lg font-bold text-emerald-600" data-testid="total-direct-chf">
                          {formatCurrency(serviceBreakdown.reduce((sum, s) => sum + (s.direct_chf || 0), 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers">
          <Card>
            <CardHeader>
              <CardTitle>Zahlungen pro Kunde</CardTitle>
              <CardDescription>Aufschlüsselung nach Kunden</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Laden...</div>
              ) : customerSummary.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine Kundendaten gefunden
                </div>
              ) : (
                <div className="space-y-3" data-testid="customer-summary-list">
                  {customerSummary.map((customer) => (
                    <div
                      key={customer.customer_id}
                      className="border rounded-lg overflow-hidden"
                      data-testid={`customer-summary-${customer.customer_id}`}
                    >
                      <div
                        className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                        onClick={() => setExpandedCustomer(
                          expandedCustomer === customer.customer_id ? null : customer.customer_id
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{customer.customer_name}</p>
                            <p className="text-sm text-gray-500">{customer.customer_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-lg font-bold">{formatCurrency(customer.total_amount)}</p>
                            <p className="text-xs text-gray-500">{customer.transaction_count} Transaktionen</p>
                          </div>
                          {expandedCustomer === customer.customer_id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedCustomer === customer.customer_id && (
                        <div className="p-4 border-t bg-white">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="p-3 bg-green-50 rounded-lg">
                              <p className="text-xs text-green-600 font-medium">Bezahlt</p>
                              <p className="text-lg font-bold text-green-700">{formatCurrency(customer.paid_amount)}</p>
                            </div>
                            <div className="p-3 bg-yellow-50 rounded-lg">
                              <p className="text-xs text-yellow-600 font-medium">Offen</p>
                              <p className="text-lg font-bold text-yellow-700">{formatCurrency(customer.pending_amount)}</p>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg">
                              <p className="text-xs text-purple-600 font-medium">Miete</p>
                              <p className="text-lg font-bold text-purple-700">{customer.rent_count} Rechnungen</p>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <p className="text-xs text-blue-600 font-medium">Services</p>
                              <p className="text-lg font-bold text-blue-700">{customer.services_count} Rechnungen</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Marketing Tab */}
        <TabsContent value="marketing">
          <div className="space-y-6">
            {/* Wiederkehrer-Quote + CHF-Umsatz-Impact (Modul: marketing) */}
            {marketingEnabled && returnerImpact && (
              <div className="space-y-4" data-testid="returner-impact-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Wiederkehrer-Quote &amp; CHF-Umsatz-Impact</h3>
                    <Badge variant="secondary" className="ml-1">letzte {returnerImpact.period_days} Tage</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadReturnerImpact('csv')} disabled={returnerExporting === 'csv'} data-testid="returner-export-csv">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadReturnerImpact('pdf')} disabled={returnerExporting === 'pdf'} data-testid="returner-export-pdf">
                      <Download className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200" data-testid="returner-rate-card">
                    <CardContent className="p-6">
                      <p className="text-sm text-emerald-700 font-medium">Wiederkehrer-Quote</p>
                      <p className="text-3xl font-bold text-emerald-900">{returnerImpact.returner_rate}%</p>
                      <p className="text-xs text-emerald-700/80 mt-1">{formatStat(returnerImpact.returning_customers)} von {formatStat(returnerImpact.active_customers)} aktiven Kunden kommen regelmässig</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200" data-testid="monthly-impact-card">
                    <CardContent className="p-6">
                      <p className="text-sm text-teal-700 font-medium">CHF-Umsatz-Impact / Monat</p>
                      <p className="text-3xl font-bold text-teal-900">{formatCurrency(returnerImpact.monthly_impact_chf)}</p>
                      <p className="text-xs text-teal-700/80 mt-1">Umsatz der Wiederkehrer, auf den Monat gerechnet</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-testid="returner-revenue-card">
                    <CardContent className="p-6">
                      <p className="text-sm text-blue-700 font-medium">Umsatz Wiederkehrer</p>
                      <p className="text-3xl font-bold text-blue-900">{formatCurrency(returnerImpact.revenue_returners_chf)}</p>
                      <p className="text-xs text-blue-700/80 mt-1">Ø {formatCurrency(returnerImpact.avg_revenue_per_returner_chf)} pro Wiederkehrer</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200" data-testid="activation-rate-card">
                    <CardContent className="p-6">
                      <p className="text-sm text-gray-600 font-medium">Aktivierungs-Quote</p>
                      <p className="text-3xl font-bold text-gray-900">{returnerImpact.activation_rate}%</p>
                      <p className="text-xs text-gray-600/80 mt-1">{formatStat(returnerImpact.active_customers)} von {formatStat(returnerImpact.total_customers)} Kunden aktiv</p>
                    </CardContent>
                  </Card>
                </div>

                <Card data-testid="returner-trend-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Verlauf: Wiederkehrer &amp; Umsatz pro Monat
                    </CardTitle>
                    <CardDescription>Monatliche Wiederkehrer-Zahl und ihr CHF-Umsatz</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {returnerImpact.trend?.length > 0 ? (
                      <div className="space-y-3">
                        {(() => {
                          const maxRev = Math.max(...returnerImpact.trend.map(t => t.revenue_chf), 1);
                          return returnerImpact.trend.map((t) => (
                            <div key={t.month} className="flex items-center gap-3" data-testid={`returner-trend-row-${t.month}`}>
                              <span className="w-20 text-sm text-gray-600 shrink-0">{t.month}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 flex items-center justify-end pr-2"
                                  style={{ width: `${Math.max((t.revenue_chf / maxRev) * 100, 4)}%` }}
                                >
                                  <span className="text-xs font-medium text-white whitespace-nowrap">{formatCurrency(t.revenue_chf)}</span>
                                </div>
                              </div>
                              <span className="w-28 text-right text-sm text-gray-700 shrink-0">{formatStat(t.returners)} Wiederkehrer</span>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-6">Noch keine Daten vorhanden.</p>
                    )}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Umsatz nach Angebot */}
                  <Card data-testid="returner-module-revenue-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-blue-600" />
                        Umsatz nach Angebot
                      </CardTitle>
                      <CardDescription>Welche Angebote bringen bei Wiederkehrern den meisten Umsatz</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {returnerImpact.module_revenue?.length > 0 ? (
                        <div className="space-y-3">
                          {returnerImpact.module_revenue.map((m, i) => (
                            <div key={m.label} className="space-y-1" data-testid={`module-revenue-row-${i}`}>
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium text-gray-800">{m.label}</span>
                                <span className="text-gray-600">{formatCurrency(m.revenue_chf)} · {m.pct}%</span>
                              </div>
                              <Progress value={m.pct} className="h-2" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-6">Noch keine Umsätze vorhanden.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Abwanderung droht */}
                  <Card data-testid="returner-at-risk-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserMinus className="w-5 h-5 text-red-600" />
                        Abwanderung droht
                        {returnerImpact.at_risk_count > 0 && (
                          <Badge variant="destructive" className="ml-1">{returnerImpact.at_risk_count}</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>Treue Kunden, die seit über 30 Tagen nicht mehr da waren – direkt per E-Mail oder WhatsApp zurückholen</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {returnerImpact.at_risk?.length > 0 ? (
                        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                          {returnerImpact.at_risk.map((a) => {
                            const wb = winbackState[a.customer_id];
                            const alreadySent = wb === 'sent' || !!a.winback_sent_at;
                            const sending = wb === 'sending';
                            return (
                            <div key={a.customer_id} className="flex items-center justify-between gap-3 py-2 flex-wrap" data-testid={`at-risk-row-${a.customer_id}`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{a.name}</p>
                                <p className="text-xs text-gray-500">Letzter Besuch: {a.last_visit} · {a.visits} Besuche</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-auto">
                                <div className="text-right">
                                  <Badge variant="outline" className="border-red-200 text-red-700">{a.days_since_visit} Tage</Badge>
                                  <p className="text-xs text-gray-500 mt-1">{formatCurrency(a.revenue_chf)}</p>
                                </div>
                                {alreadySent ? (
                                  <Badge variant="outline" className="border-emerald-200 text-emerald-700 gap-1" data-testid={`winback-sent-${a.customer_id}`}>
                                    <Check className="w-3 h-3" /> Eingeladen
                                  </Badge>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="outline" size="sm" className="h-8 px-2"
                                      disabled={sending || !a.email}
                                      title={a.email ? 'Per E-Mail einladen' : 'Keine E-Mail hinterlegt'}
                                      onClick={() => sendWinback(a, 'email')}
                                      data-testid={`winback-email-${a.customer_id}`}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline" size="sm" className="h-8 px-2"
                                      disabled={sending || !a.phone}
                                      title={a.phone ? 'Per WhatsApp einladen' : 'Keine Telefonnummer hinterlegt'}
                                      onClick={() => sendWinback(a, 'whatsapp')}
                                      data-testid={`winback-whatsapp-${a.customer_id}`}
                                    >
                                      <MessageCircle className="w-4 h-4 text-green-600" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-6">Aktuell keine gefährdeten Kunden.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Kunden mit Quelle</p>
                      <p className="text-3xl font-bold text-purple-900">{formatStat(referralStats?.total_with_source)}</p>
                    </div>
                    <Megaphone className="w-12 h-12 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Ohne Quelle</p>
                      <p className="text-3xl font-bold text-gray-900">{formatStat(referralStats?.total_without_source)}</p>
                    </div>
                    <Users className="w-12 h-12 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Gesamt Kunden</p>
                      <p className="text-3xl font-bold text-blue-900">{formatStat(referralStats?.total_customers)}</p>
                    </div>
                    <TrendingUp className="w-12 h-12 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-purple-600" />
                    Wie haben Kunden von uns erfahren?
                  </CardTitle>
                  <CardDescription>Verteilung der Marketing-Quellen</CardDescription>
                </CardHeader>
                <CardContent>
                  {referralStats?.statistics?.length > 0 ? (
                    <div className="flex flex-col items-center">
                      {/* Simple CSS Pie Chart */}
                      <div className="relative w-64 h-64 mb-6">
                        <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                          {(() => {
                            const colors = [
                              '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444',
                              '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'
                            ];
                            let cumulativePercent = 0;
                            return referralStats.statistics.map((stat, index) => {
                              const percent = stat.percentage;
                              const startPercent = cumulativePercent;
                              cumulativePercent += percent;
                              
                              // Calculate SVG arc
                              const startAngle = (startPercent / 100) * 360;
                              const endAngle = (cumulativePercent / 100) * 360;
                              const largeArcFlag = percent > 50 ? 1 : 0;
                              
                              const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                              const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                              const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                              const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                              
                              const pathD = percent >= 100
                                ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                                : `M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
                              
                              return (
                                <path
                                  key={stat.source_id}
                                  d={pathD}
                                  fill={colors[index % colors.length]}
                                  className="transition-opacity hover:opacity-80 cursor-pointer"
                                  data-testid={`pie-slice-${index}`}
                                >
                                  <title>{stat.name}: {stat.count} ({stat.percentage}%)</title>
                                </path>
                              );
                            });
                          })()}
                        </svg>
                        {/* Center Circle (Donut effect) */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-24 h-24 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                            <span className="text-2xl font-bold text-gray-900">{formatStat(referralStats?.total_with_source)}</span>
                            <span className="text-xs text-gray-500">Kunden</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Legend */}
                      <div className="flex flex-wrap justify-center gap-3">
                        {referralStats.statistics.map((stat, index) => {
                          const colors = [
                            '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444',
                            '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'
                          ];
                          return (
                            <div key={stat.source_id} className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: colors[index % colors.length] }}
                              />
                              <span className="text-sm text-gray-600">{stat.icon} {stat.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <PieChart className="w-16 h-16 mb-4 opacity-30" />
                      <p>Noch keine Daten verfügbar</p>
                      <p className="text-sm">Kunden müssen bei der Registrierung eine Quelle angeben</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Detailed Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Detaillierte Übersicht
                  </CardTitle>
                  <CardDescription>Anzahl und Prozent pro Marketing-Quelle</CardDescription>
                </CardHeader>
                <CardContent>
                  {referralStats?.statistics?.length > 0 ? (
                    <div className="space-y-4">
                      {referralStats.statistics.map((stat, index) => {
                        const colors = [
                          'bg-purple-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-red-500',
                          'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-lime-500'
                        ];
                        return (
                          <div key={stat.source_id} data-testid={`referral-stat-${index}`}>
                            <div className="flex justify-between items-center mb-1">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{stat.icon}</span>
                                <span className="font-medium text-gray-900">{stat.name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="secondary">{stat.count} Kunden</Badge>
                                <span className="text-sm font-semibold text-gray-700">{stat.percentage}%</span>
                              </div>
                            </div>
                            <Progress 
                              value={stat.percentage} 
                              className="h-2"
                              style={{ 
                                '--progress-color': colors[index % colors.length].replace('bg-', '').replace('-500', '') 
                              }}
                            />
                          </div>
                        );
                      })}
                      
                      {/* Kunden ohne Quelle */}
                      {referralStats.total_without_source > 0 && (
                        <div className="pt-4 border-t mt-4">
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">❓</span>
                              <span className="font-medium text-gray-500">Keine Angabe</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline">{referralStats.total_without_source} Kunden</Badge>
                              <span className="text-sm text-gray-500">
                                {referralStats.total_customers > 0 
                                  ? Math.round((referralStats.total_without_source / referralStats.total_customers) * 100) 
                                  : 0}%
                              </span>
                            </div>
                          </div>
                          <Progress 
                            value={referralStats.total_customers > 0 
                              ? (referralStats.total_without_source / referralStats.total_customers) * 100 
                              : 0} 
                            className="h-2 bg-gray-200"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <BarChart3 className="w-16 h-16 mb-4 opacity-30" />
                      <p>Noch keine Statistiken</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend Chart */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      Monatlicher Trend {selectedMarketingYear}
                    </CardTitle>
                    <CardDescription>Neukunden pro Monat nach Marketing-Quelle</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedMarketingYear(y => y - 1)}
                    >
                      ←
                    </Button>
                    <span className="px-3 py-1 bg-gray-100 rounded text-sm font-medium">{selectedMarketingYear}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedMarketingYear(y => y + 1)}
                      disabled={selectedMarketingYear >= new Date().getFullYear()}
                    >
                      →
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {monthlyReferralStats?.monthly_data ? (
                  <div className="space-y-4">
                    {/* Month Headers */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">Quelle</TableHead>
                            {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'].map((month, idx) => (
                              <TableHead key={idx} className="text-center w-[60px]">{month}</TableHead>
                            ))}
                            <TableHead className="text-center w-[80px]">Gesamt</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {monthlyReferralStats.sources?.map((source, idx) => {
                            const colors = [
                              'bg-purple-100 text-purple-700', 'bg-cyan-100 text-cyan-700', 'bg-emerald-100 text-emerald-700',
                              'bg-amber-100 text-amber-700', 'bg-red-100 text-red-700', 'bg-pink-100 text-pink-700',
                              'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700', 'bg-orange-100 text-orange-700'
                            ];
                            let yearTotal = 0;
                            
                            return (
                              <TableRow key={source.id}>
                                <TableCell className="font-medium">
                                  <span className="mr-2">{source.icon}</span>
                                  {source.name}
                                </TableCell>
                                {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((month) => {
                                  const count = monthlyReferralStats.monthly_data[month]?.[source.name] || 0;
                                  yearTotal += count;
                                  return (
                                    <TableCell key={month} className="text-center">
                                      {count > 0 ? (
                                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${colors[idx % colors.length]}`}>
                                          {count}
                                        </span>
                                      ) : (
                                        <span className="text-gray-300">-</span>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                <TableCell className="text-center font-bold">
                                  {yearTotal > 0 ? (
                                    <Badge variant="secondary">{yearTotal}</Badge>
                                  ) : (
                                    <span className="text-gray-300">0</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Totals Row */}
                          <TableRow className="bg-gray-50 font-bold">
                            <TableCell>Gesamt</TableCell>
                            {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((month) => {
                              const monthTotal = Object.values(monthlyReferralStats.monthly_data[month] || {}).reduce((a, b) => a + b, 0);
                              return (
                                <TableCell key={month} className="text-center">
                                  {monthTotal > 0 ? (
                                    <span className="font-bold text-purple-700">{monthTotal}</span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-center">
                              <Badge className="bg-purple-600">
                                {Object.values(monthlyReferralStats.monthly_data).reduce((total, monthData) => 
                                  total + Object.values(monthData || {}).reduce((a, b) => a + b, 0), 0
                                )}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Visual Bar Chart */}
                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm font-medium text-gray-600 mb-4">Monatliche Verteilung</p>
                      <div className="flex items-end gap-2 h-32">
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((month, idx) => {
                          const monthTotal = Object.values(monthlyReferralStats.monthly_data[month] || {}).reduce((a, b) => a + b, 0);
                          const maxMonth = Math.max(...['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => 
                            Object.values(monthlyReferralStats.monthly_data[m] || {}).reduce((a, b) => a + b, 0)
                          ));
                          const heightPercent = maxMonth > 0 ? (monthTotal / maxMonth) * 100 : 0;
                          const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                          
                          return (
                            <div key={month} className="flex-1 flex flex-col items-center">
                              <div className="w-full flex flex-col items-center">
                                {monthTotal > 0 && (
                                  <span className="text-xs font-medium text-purple-700 mb-1">{monthTotal}</span>
                                )}
                                <div 
                                  className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t transition-all"
                                  style={{ height: `${Math.max(heightPercent, monthTotal > 0 ? 10 : 0)}%`, minHeight: monthTotal > 0 ? '8px' : '0px' }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 mt-2">{monthNames[idx]}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Calendar className="w-16 h-16 mb-4 opacity-30" />
                    <p>Keine monatlichen Daten verfügbar</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
